import { AfterViewInit, Component, OnDestroy, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { ActivatedRoute, ParamMap, Router } from '@angular/router';
import { NgbModal, NgbModalConfig } from '@ng-bootstrap/ng-bootstrap';
import { EMPTY, Subject, Subscription, timer, of } from 'rxjs';
import {
  catchError,
  debounceTime,
  distinctUntilChanged,
  exhaustMap,
  filter,
  finalize,
  map,
  switchMap,
  take,
  takeUntil,
  tap,
} from 'rxjs/operators';
import { ApiService } from 'src/app/services/api/api.service';
import { AuthService } from 'src/app/services/auth.service';
import {
  ConnectivityListContext,
  ConnectivityStatusFilter,
  clientListContextsEqual,
  interfaceIdsForStatus,
  parseClientListContext,
  serializeClientListContext,
} from './connectivity-list-context';

type StatusResolution = 'none' | 'waiting' | 'applied' | 'stalled' | 'unavailable';
type StatusSummarySource = 'initial' | 'retry' | 'poll' | 'refresh';

@Component({
  selector: 'app-institutions',
  templateUrl: './institutions.component.html',
  styleUrls: ['./institutions.component.scss'],
})
export class InstitutionsComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('modalDetail') modalDetail?: TemplateRef<unknown>;

  institutions: any[] = [];
  totalRecords: number = 0;
  currentPage: number = 1;
  pageSize: number = 50;
  searchInput: string = '';
  search: string = '';
  loading: boolean = false;

  groups: any[] = [];
  groupId!: number;
  nodeId: number | null = null;
  trafficData!: any;
  institution: any = null;
  userId!: number;

  trafficSummary: any = null;
  summaryRefreshing: boolean = false;
  selectedStatusFilter: ConnectivityStatusFilter | null = null;
  filteredInterfaceIds: number[] = [];
  statusListNotice: 'pending' | 'unavailable' | null = null;

  private statusIntent: ConnectivityStatusFilter | null = null;
  private statusResolution: StatusResolution = 'none';
  private statusRetryUsed = false;
  private summaryPendingRetries: number = 0;
  private summaryRetrySubscription: Subscription | null = null;
  lastVisitedClientServiceId: number | null = null;
  private returnFocusId: number | null = null;
  private returnScrollPending = false;
  private returnScrollTimer: ReturnType<typeof setTimeout> | null = null;

  private readonly loadInstitutions$ = new Subject<number>();
  private readonly searchInput$ = new Subject<string>();
  private readonly destroy$ = new Subject<void>();
  private stopPolling$ = new Subject<void>();
  private stopSummaryPolling$ = new Subject<void>();
  private loadSeq = 0;
  private requestedPage = 1;
  private didClampPage = false;
  private groupsReady = false;
  private viewReady = false;
  private latestParams: ParamMap | null = null;
  private appliedContext: ConnectivityListContext | null = null;
  private lastWrittenQuery: Record<string, string | number> | null = null;
  private appliedGroupId: number | null = null;

  constructor(
    private readonly _api: ApiService,
    private readonly _authService: AuthService,
    private readonly _modalService: NgbModal,
    private readonly _router: Router,
    private readonly _route: ActivatedRoute,
    config: NgbModalConfig,
  ) {
    config.backdrop = 'static';
    config.keyboard = false;
  }

  ngOnInit(): void {
    this.setupLoadStream();
    this.setupSearchStream();
    this._route.queryParamMap.pipe(takeUntil(this.destroy$)).subscribe((params) => {
      this.latestParams = params;
      if (this.groupsReady) this.onQueryParams(params);
    });
    this.getUserGroupsByToken();
  }

  ngAfterViewInit(): void {
    this.viewReady = true;
    this.consumeReturnFocus();
  }

  private setupLoadStream(): void {
    this.loadInstitutions$
      .pipe(
        switchMap((seq) => {
          if (!this.shouldRequestList()) return EMPTY;
          this.stopTrafficPolling();
          this.loading = true;

          const load$ = this.statusIntent
            ? this._api.userGroup.getUsersGroupedByUserGroupFiltered(this.groupId, {
                page: this.currentPage,
                perPage: this.pageSize,
                search: this.search,
                institutionInterfaceIds: this.filteredInterfaceIds,
              })
            : this._api.userGroup.getUsersGroupedByUserGroup(this.groupId, {
                page: this.currentPage,
                perPage: this.pageSize,
                search: this.search,
              });

          return load$.pipe(
            map((res) => ({ res, seq })),
            catchError(() =>
              of({
                res: { data: [], total: 0, currentPage: 1, perPage: this.pageSize },
                seq,
              }),
            ),
            finalize(() => {
              if (seq === this.loadSeq) this.loading = false;
            }),
          );
        }),
        takeUntil(this.destroy$),
      )
      .subscribe(({ res, seq }) => {
        if (seq !== this.loadSeq) return;
        this.applyListResponse(res);
      });
  }

  private setupSearchStream(): void {
    this.searchInput$
      .pipe(
        debounceTime(400),
        distinctUntilChanged(),
        filter((value) => value === '' || value.length >= 2),
        takeUntil(this.destroy$),
      )
      .subscribe((value) => {
        this.search = value;
        this.currentPage = 1;
        this.forgetSelected();
        this.syncUrl();
        this.loadInstitutions();
      });
  }

  getUserGroupsByToken(): void {
    this.groups = [];
    this._api.userGroup
      .getUserGroupsByToken()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (resp) => {
          this.groups = resp.data ?? [];
          this.groupsReady = true;
          this.onQueryParams(this.latestParams ?? this._route.snapshot.queryParamMap);
        },
        error: (err) => console.warn(err),
      });
  }

  loadInstitutions(): void {
    if (!this.groupId) return;
    if (!this.shouldRequestList()) {
      if (this.statusIntent && this.statusResolution === 'applied') {
        this.institutions = [];
        this.totalRecords = 0;
        this.loading = false;
        this.invalidateListLoad();
        this.stopTrafficPolling();
      }
      return;
    }
    this.requestedPage = this.currentPage;
    this.loadSeq += 1;
    this.loadInstitutions$.next(this.loadSeq);
  }

  private shouldRequestList(): boolean {
    if (!this.statusIntent) return true;
    return this.statusResolution === 'applied' && this.filteredInterfaceIds.length > 0;
  }

  private invalidateListLoad(): void {
    this.loadSeq += 1;
  }

  onGroupChange(groupId: number): void {
    const id = Number(groupId);
    if (!Number.isInteger(id) || id <= 0) return;
    if (id === this.appliedGroupId) return;

    this.appliedGroupId = id;
    this.assignGroup(id);
    this.currentPage = 1;
    this.searchInput = '';
    this.search = '';
    this.forgetSelected();
    this.selectedStatusFilter = null;
    this.statusIntent = null;
    this.filteredInterfaceIds = [];
    this.statusListNotice = null;
    this.statusResolution = 'none';
    this.didClampPage = false;
    this.trafficSummary = null;
    this.summaryPendingRetries = 0;
    this.syncUrl();
    this.loadInstitutions();
    this.startSummaryPolling(2000, 30000);
  }

  onPageChange(page: number): void {
    if (page === this.requestedPage) return;
    this.currentPage = page;
    this.forgetSelected();
    this.syncUrl();
    this.loadInstitutions();
  }

  onPageSizeChange(event: any): void {
    this.pageSize = Number.parseInt(event.target.value, 10);
    this.currentPage = 1;
    this.forgetSelected();
    this.syncUrl();
    this.loadInstitutions();
  }

  onSearchChange(value: string): void {
    this.searchInput = value;
    this.searchInput$.next(value);
  }

  onStatusFilterClick(statusFilter: ConnectivityStatusFilter): void {
    if (this.selectedStatusFilter === statusFilter) {
      this.clearStatusFilter();
      return;
    }

    const ids = interfaceIdsForStatus(this.trafficSummary, statusFilter);
    if (!ids.length) return;

    this.statusIntent = statusFilter;
    this.selectedStatusFilter = statusFilter;
    this.statusResolution = 'applied';
    this.statusListNotice = null;
    this.filteredInterfaceIds = ids;
    this.currentPage = 1;
    this.forgetSelected();
    this.syncUrl();
    this.loadInstitutions();
  }

  clearStatusFilter(reload: boolean = true): void {
    const hadFilter = !!this.selectedStatusFilter || !!this.statusIntent;
    const unresolved =
      !!this.statusIntent &&
      (this.statusResolution === 'waiting' ||
        this.statusResolution === 'stalled' ||
        this.statusResolution === 'unavailable');
    this.selectedStatusFilter = null;
    this.statusIntent = null;
    this.filteredInterfaceIds = [];
    this.statusListNotice = null;
    this.statusResolution = 'none';
    this.statusRetryUsed = false;
    if (hadFilter) this.forgetSelected();
    if (unresolved) this.startSummaryPolling(2000, 30000);
    if (reload && hadFilter) {
      this.currentPage = 1;
      this.syncUrl();
      this.loadInstitutions();
    }
  }

  get summaryFilterLabel(): string {
    switch (this.selectedStatusFilter) {
      case 'active':
        return 'activas';
      case 'inactive':
        return 'inactivas';
      default:
        return '';
    }
  }

  get summaryInactiveCount(): number {
    if (!this.trafficSummary?.summary) return 0;
    return (this.trafficSummary.summary.inactive ?? 0) + (this.trafficSummary.summary.unknown ?? 0);
  }

  private startSummaryPolling(initialDelay: number, period: number): void {
    this.stopSummaryPolling();
    const stop$ = this.stopSummaryPolling$;
    this.summaryPendingRetries = 0;

    timer(initialDelay, period)
      .pipe(
        takeUntil(this.destroy$),
        takeUntil(stop$),
        exhaustMap(() => this.fetchSummary()),
      )
      .subscribe((summary) => this.onSummaryTick(summary));
  }

  private stopSummaryPolling(): void {
    this.stopSummaryPolling$.next();
    this.stopSummaryPolling$.complete();
    this.stopSummaryPolling$ = new Subject<void>();
    this.summaryRetrySubscription?.unsubscribe();
    this.summaryRetrySubscription = null;
  }

  private fetchSummary() {
    return this._api.router.getInstitutionTrafficStatusSummary(this.groupId).pipe(
      take(1),
      catchError(() => of(null)),
    );
  }

  private handleSummaryResponse(summary: any): void {
    const hadNoPreviousData = this.trafficSummary === null || this.trafficSummary?.state === 'pending';
    this.trafficSummary = summary;

    if (summary?.state === 'pending' && hadNoPreviousData && this.summaryPendingRetries < 1) {
      this.summaryPendingRetries++;
      this.summaryRetrySubscription?.unsubscribe();
      this.summaryRetrySubscription = timer(15000)
        .pipe(
          take(1),
          takeUntil(this.destroy$),
          takeUntil(this.stopSummaryPolling$),
          switchMap(() => this.fetchSummary()),
        )
        .subscribe((next) => {
          if (next && !this.statusIntent) this.handleSummaryResponse(next);
        });
    } else if (summary?.state !== 'pending') {
      this.summaryPendingRetries = 0;
    }
  }

  refreshSummary(): void {
    if (this.summaryRefreshing || !this.groupId) return;
    this.summaryRefreshing = true;

    this.fetchSummary()
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => {
          this.summaryRefreshing = false;
        }),
      )
      .subscribe((summary) => {
        if (this.statusIntent && this.statusResolution !== 'applied') {
          this.onStatusSummary(summary, 'refresh');
          return;
        }
        if (this.statusIntent && this.statusResolution === 'applied') {
          if (summary?.state === 'fresh') this.trafficSummary = summary;
          return;
        }
        if (!summary) return;
        this.trafficSummary = summary;
        this.summaryPendingRetries = 0;
        if (summary.state === 'pending') this.handleSummaryResponse(summary);
      });
  }

  private startTrafficPolling(): void {
    const uniqueIps = this.institutions
      .map((institution) => institution.institutionInterface?.ip)
      .filter((ip, index, array) => !!ip && array.indexOf(ip) === index);

    if (!uniqueIps.length || !this.groupId) return;

    timer(0, 6000)
      .pipe(
        takeUntil(this.destroy$),
        takeUntil(this.stopPolling$),
        exhaustMap(() =>
          this._api.router.getInstitutionTrafficLive(this.groupId, uniqueIps).pipe(
            takeUntil(this.destroy$),
            takeUntil(this.stopPolling$),
            tap((results) => this.adaptLiveResponse(results)),
            catchError(() => {
              uniqueIps.forEach((ip) => this.updateTrafficDataError(ip));
              return of(null);
            }),
          ),
        ),
      )
      .subscribe();
  }

  private stopTrafficPolling(): void {
    this.stopPolling$.next();
    this.stopPolling$.complete();
    this.stopPolling$ = new Subject<void>();
  }

  private adaptLiveResponse(results: any[]): void {
    if (!results) return;

    results.forEach((ipResult) => {
      if (ipResult.state === 'collector_error') {
        this.updateTrafficDataError(ipResult.ip);
        return;
      }
      if (ipResult.state === 'fresh') {
        this.updateTrafficData(ipResult.interfaces || []);
      }
    });
  }

  updateTrafficData(newTrafficData: any[]): void {
    this.institutions.forEach((institution) => {
      const matchingTraffic = newTrafficData.find(
        (trafficItem) => trafficItem.id === institution.institutionInterface?.id,
      );

      if (matchingTraffic) {
        institution.trafficData = matchingTraffic;
        if (this.institution && this.institution.institutionInterface?.id === matchingTraffic.id) {
          this.trafficData = this.getTraffic(this.institution.id);
        }
      }
    });
  }

  updateTrafficDataError(ip: string): void {
    this.institutions.forEach((institution) => {
      if (institution.institutionInterface?.ip === ip) {
        institution.trafficData = null;
      }

      if (
        this.institution &&
        this.institution.id === institution.id &&
        this.institution.institutionInterface?.ip === ip
      ) {
        this.trafficData = this.getTraffic(this.institution.id);
      }
    });
  }

  isTrafficLoading(data: any): boolean {
    return data.trafficData === undefined;
  }

  isTrafficActive(data: any): boolean {
    return data.trafficData?.status === 'active';
  }

  getTrafficStatusLabel(data: any): string {
    return this.isTrafficActive(data) ? 'ACTIVO' : 'INACTIVO';
  }

  getTrafficStatusClass(data: any): string {
    return this.isTrafficActive(data) ? 'text-bg-success' : 'text-bg-danger';
  }

  getTrafficPulseClass(data: any): string {
    return this.isTrafficActive(data) ? 'text-success' : 'text-danger';
  }

  getTrafficStatusDurationLabel(data: any): string {
    const duration = data.trafficData?.statusDuration;
    const source = data.trafficData?.statusSource;
    if (duration && source !== 'fallback_redis') return `Desde hace ${duration}`;
    return 'Sin tiempo registrado';
  }

  private getTraffic(userId: number) {
    const data = {
      tx: 0,
      rx: 0,
      textTx: '',
      textRx: '',
      status: 'INACTIVO',
      statusDuration: null as string | null,
    };

    const item = this.institutions.find((institution) => institution.id === userId);
    if (!item?.trafficData) return data;

    data.status = item.trafficData.status;
    if (item.trafficData.status === 'active' && item.trafficData.tx !== null && item.trafficData.rx !== null) {
      data.tx = +String(item.trafficData.tx).split(' ')[0];
      data.rx = +String(item.trafficData.rx).split(' ')[0];
      data.textTx = item.trafficData.tx;
      data.textRx = item.trafficData.rx;
    }
    data.statusDuration = item.trafficData.statusDuration ?? null;
    return data;
  }

  formatLocality(data: any): string {
    if (!data?.ubigeoDetail) return '—';
    const district = data.ubigeoDetail.district?.name?.trim();
    const province = data.ubigeoDetail.province?.name?.trim();
    const dept = data.ubigeoDetail.departament?.name?.trim();

    const parts: string[] = [];
    if (district) parts.push(district);
    if (province && province !== district) parts.push(province);
    if (dept && dept !== province && dept !== district) parts.push(dept);
    return parts.length > 0 ? parts.join(' • ') : '—';
  }

  trackById(index: number, item: any): string {
    return item.id;
  }

  openRouter(ip: string): void {
    if (ip) window.open(`http://${ip}`, '_blank', 'noopener,noreferrer');
  }

  getClientServiceId(institution: any): number | null {
    const id = Number(institution?.institutionInterface?.service?.id);
    return Number.isInteger(id) && id > 0 ? id : null;
  }

  listContextQueryParams(): Record<string, string | number> {
    return serializeClientListContext(this.snapshotContext());
  }

  onViewHistory(clientServiceId: number): void {
    if (!clientServiceId) return;
    this.lastVisitedClientServiceId = clientServiceId;
    this._modalService.dismissAll();
    this.institution = null;
    this._router.navigate(['/servicios-ie', clientServiceId, 'historico'], {
      queryParams: serializeClientListContext({
        ...this.snapshotContext(),
        focus: clientServiceId,
      }),
    });
  }

  openDetails(content: any, institution: any): void {
    this.institution = institution;
    this.userId = this.institution.id;
    this.trafficData = this.getTraffic(institution.id);
    const serviceId = this.getClientServiceId(institution);
    if (serviceId) this.lastVisitedClientServiceId = serviceId;
    this._modalService.open(content, { centered: true, size: 'xl' });
  }

  onUserClose(close: (reason: string) => void): void {
    close('Save click');
    this.institution = null;
  }

  onUserDismiss(dismiss: (reason: string) => void): void {
    dismiss('Cross click');
    this.institution = null;
  }

  ngOnDestroy(): void {
    this.clearReturnScrollTimer();
    this.stopTrafficPolling();
    this.stopSummaryPolling();
    this.summaryRetrySubscription?.unsubscribe();
    this.destroy$.next();
    this.destroy$.complete();
  }

  private onQueryParams(params: ParamMap): void {
    if (!this.groupsReady) return;
    const parsed = parseClientListContext(params);
    const context: ConnectivityListContext = {
      ...parsed,
      groupId: this.resolveGroup(parsed.groupId),
    };
    this.lastWrittenQuery = queryRecordFromParamMap(params);
    const listContext = { ...context, focus: null };
    if (this.appliedContext && clientListContextsEqual(this.appliedContext, listContext)) {
      if (context.focus != null) this.armReturnFocus(context.focus);
      return;
    }
    this.applyContext(context);
  }

  private resolveGroup(requested: number | null): number | null {
    if (!this.groups.length) return null;
    if (requested != null && this.groups.some((group) => Number(group.id) === requested)) return requested;
    return Number(this.groups[0].id);
  }

  private assignGroup(groupId: number | null): void {
    if (groupId == null) return;
    this.groupId = groupId;
    const selected = this.groups.find((group) => Number(group.id) === groupId);
    this.nodeId = selected?.groupRouter?.nodeId ?? null;
  }

  private applyContext(context: ConnectivityListContext): void {
    this.armReturnFocus(context.focus);
    this.appliedGroupId = context.groupId;
    this.assignGroup(context.groupId);
    this.pageSize = context.pageSize;
    this.search = context.search;
    this.searchInput = context.search;
    this.currentPage = context.page;
    this.institution = null;
    this.statusIntent = context.status;
    this.selectedStatusFilter = context.status;
    this.filteredInterfaceIds = [];
    this.statusListNotice = null;
    this.statusResolution = context.status ? 'waiting' : 'none';
    this.statusRetryUsed = false;
    this.didClampPage = false;
    this.institutions = [];
    this.totalRecords = 0;
    this.trafficSummary = null;
    this.summaryPendingRetries = 0;
    this.invalidateListLoad();
    this._modalService.dismissAll();
    this.syncUrl();

    if (!context.groupId) {
      this.loading = false;
      this.stopSummaryPolling();
      this.stopTrafficPolling();
      return;
    }

    if (context.status) {
      this.beginStatusRestore();
      return;
    }

    this.loadInstitutions();
    this.startSummaryPolling(2000, 30000);
  }

  private beginStatusRestore(): void {
    this.stopSummaryPolling();
    this.invalidateListLoad();
    this.statusResolution = 'waiting';
    this.statusRetryUsed = false;
    this.statusListNotice = null;
    this.loading = true;
    this.institutions = [];
    this.totalRecords = 0;
    this.filteredInterfaceIds = [];
    this.selectedStatusFilter = this.statusIntent;
    this.trafficSummary = null;
    const stop$ = this.stopSummaryPolling$;

    this.fetchSummary()
      .pipe(takeUntil(this.destroy$), takeUntil(stop$))
      .subscribe((summary) => this.onStatusSummary(summary, 'initial'));
  }

  private scheduleStatusRetry(): void {
    const stop$ = this.stopSummaryPolling$;
    this.summaryRetrySubscription?.unsubscribe();
    this.summaryRetrySubscription = timer(15000)
      .pipe(
        take(1),
        takeUntil(this.destroy$),
        takeUntil(stop$),
        switchMap(() => this.fetchSummary()),
      )
      .subscribe((summary) => this.onStatusSummary(summary, 'retry'));
  }

  private onSummaryTick(summary: any): void {
    if (this.statusIntent) {
      this.onStatusSummary(summary, 'poll');
      return;
    }
    if (!summary) return;
    this.handleSummaryResponse(summary);
  }

  private onStatusSummary(summary: any, source: StatusSummarySource): void {
    if (!this.statusIntent) return;

    if (this.statusResolution === 'applied') {
      if (summary?.state === 'fresh') this.trafficSummary = summary;
      return;
    }

    if (summary?.state === 'fresh') {
      this.applyFreshStatus(summary);
      if (source !== 'poll') this.startSummaryPolling(30000, 30000);
      return;
    }

    if (summary?.state === 'pending') {
      this.trafficSummary = summary;
      this.selectedStatusFilter = this.statusIntent;
      if (!this.statusRetryUsed) {
        this.statusRetryUsed = true;
        this.scheduleStatusRetry();
        return;
      }
      if (source === 'refresh' && this.statusResolution === 'waiting') return;
      this.markStatusStalled();
      if (source !== 'poll') this.startSummaryPolling(30000, 30000);
      return;
    }

    this.markStatusUnavailable();
    if (source !== 'poll') this.startSummaryPolling(30000, 30000);
  }

  private applyFreshStatus(summary: any): void {
    this.trafficSummary = summary;
    this.summaryPendingRetries = 0;
    this.statusListNotice = null;
    this.statusResolution = 'applied';
    this.selectedStatusFilter = this.statusIntent;
    const ids = interfaceIdsForStatus(summary, this.statusIntent!);

    if (!ids.length) {
      this.filteredInterfaceIds = [];
      this.institutions = [];
      this.totalRecords = 0;
      this.currentPage = 1;
      this.loading = false;
      this.invalidateListLoad();
      this.stopTrafficPolling();
      this.syncUrl();
      this.consumeReturnFocus();
      return;
    }

    this.filteredInterfaceIds = ids;
    this.loadInstitutions();
  }

  private markStatusStalled(): void {
    this.statusResolution = 'stalled';
    this.statusListNotice = 'pending';
    this.loading = false;
    this.institutions = [];
    this.totalRecords = 0;
    this.filteredInterfaceIds = [];
    this.selectedStatusFilter = this.statusIntent;
    this.invalidateListLoad();
    this.stopTrafficPolling();
  }

  private markStatusUnavailable(): void {
    this.statusResolution = 'unavailable';
    this.statusListNotice = 'unavailable';
    this.loading = false;
    this.institutions = [];
    this.totalRecords = 0;
    this.filteredInterfaceIds = [];
    this.selectedStatusFilter = this.statusIntent;
    this.invalidateListLoad();
    this.stopTrafficPolling();
  }

  private applyListResponse(res: any): void {
    const rows = res?.data ?? [];
    const lastPage = Number(res?.lastPage);

    if (rows.length === 0 && this.currentPage > 1) {
      if (Number.isInteger(lastPage) && lastPage >= 1 && this.currentPage > lastPage) {
        if (!this.didClampPage) {
          this.didClampPage = true;
          this.institutions = [];
          this.currentPage = lastPage;
          this.syncUrl();
          this.loadInstitutions();
          return;
        }
      } else if (!this.didClampPage) {
        this.didClampPage = true;
        this.currentPage = 1;
        this.syncUrl();
      }
    } else {
      this.didClampPage = false;
      this.currentPage = Number(res?.currentPage ?? this.currentPage);
      this.pageSize = Number(res?.perPage ?? this.pageSize);
    }

    this.institutions = rows;
    this.totalRecords = res?.total ?? 0;
    this.requestedPage = this.currentPage;
    this.consumeReturnFocus();
    this.startTrafficPolling();
  }

  private snapshotContext(): ConnectivityListContext {
    return {
      groupId: this.groupId ?? null,
      page: this.currentPage,
      pageSize: this.pageSize,
      search: this.search,
      status: this.statusIntent ?? this.selectedStatusFilter,
      focus: null,
    };
  }

  private syncUrl(): void {
    const context = this.snapshotContext();
    this.appliedContext = { ...context };
    this.appliedGroupId = context.groupId;
    const next = serializeClientListContext(context);
    if (this.lastWrittenQuery && sameQueryRecord(next, this.lastWrittenQuery)) return;
    const fromUrl = queryRecordFromParamMap(this._route.snapshot.queryParamMap);
    if (
      sameQueryRecord(next, fromUrl) &&
      this.lastWrittenQuery != null &&
      sameQueryRecord(this.lastWrittenQuery, fromUrl)
    ) {
      this.lastWrittenQuery = next;
      return;
    }
    this.lastWrittenQuery = next;
    this._router.navigate([], {
      relativeTo: this._route,
      queryParams: next,
      replaceUrl: true,
    });
  }

  private forgetSelected(): void {
    this.clearReturnScrollTimer();
    this.returnFocusId = null;
    this.returnScrollPending = false;
    this.lastVisitedClientServiceId = null;
    this.institution = null;
    this._modalService.dismissAll();
  }

  isLastVisited(data: any): boolean {
    return this.getClientServiceId(data) === this.lastVisitedClientServiceId;
  }

  rowElementId(data: any): string | null {
    const id = this.getClientServiceId(data);
    return id == null ? null : `inst-ie-${id}`;
  }

  private armReturnFocus(focusId: number | null): void {
    this.clearReturnScrollTimer();
    this.returnFocusId = focusId;
    this.returnScrollPending = focusId != null;
    if (focusId == null) this.lastVisitedClientServiceId = null;
  }

  private consumeReturnFocus(): void {
    if (!this.returnScrollPending || this.returnFocusId == null || !this.viewReady) return;
    if (this.statusIntent && this.statusResolution !== 'applied') return;

    const focusId = this.returnFocusId;
    const row = this.institutions.find((institution) => this.getClientServiceId(institution) === focusId);
    this.returnScrollPending = false;
    this.returnFocusId = null;
    if (!row) {
      this.lastVisitedClientServiceId = null;
      return;
    }

    this.lastVisitedClientServiceId = focusId;
    this.scheduleReturnScroll(focusId);
  }

  private scheduleReturnScroll(id: number): void {
    this.clearReturnScrollTimer();
    this.returnScrollTimer = setTimeout(() => {
      this.returnScrollTimer = null;
      if (this.lastVisitedClientServiceId !== id) return;
      document.getElementById(`inst-ie-${id}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    });
  }

  private clearReturnScrollTimer(): void {
    if (this.returnScrollTimer == null) return;
    clearTimeout(this.returnScrollTimer);
    this.returnScrollTimer = null;
  }
}

function queryRecordFromParamMap(params: ParamMap): Record<string, string | number> {
  const record: Record<string, string | number> = {};
  params.keys.forEach((key) => {
    const value = params.get(key);
    if (value != null) record[key] = value;
  });
  return record;
}

function sameQueryRecord(
  left: Record<string, string | number>,
  right: Record<string, string | number>,
): boolean {
  const leftKeys = Object.keys(left).sort();
  const rightKeys = Object.keys(right).sort();
  if (leftKeys.length !== rightKeys.length) return false;
  return leftKeys.every((key, index) => key === rightKeys[index] && String(left[key]) === String(right[key]));
}
